import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/shared/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/shared/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/shared/hooks/use-toast";
import { apiClient } from "@/shared/services/apiClient";
import { API_ROUTES } from "@/shared/services/apiRoutes";
import { useSubscription } from "@/features/billing/contexts/SubscriptionContext";

export default function AccountPausedPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { usage, refreshSubscription } = useSubscription();
  const [isProcessing, setIsProcessing] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showRenewDialog, setShowRenewDialog] = useState(false);

  const calculateRemainingMonths = () => {
    if (!usage.pauseEndDate) return 0;

    const endDate = new Date(usage.pauseEndDate);
    const now = new Date();
    const diffInMonths = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24 * 30));

    return Math.max(0, diffInMonths);
  };

  const handleCancelSubscription = async () => {
    setIsProcessing(true);
    try {
      const response = await apiClient.post<{ ok: boolean; message: string }>(
        API_ROUTES.BILLING_CANCEL
      );

      if (!response.ok) {
        throw new Error(response.message || 'Failed to cancel subscription');
      }

      toast({
        title: "Subscription Cancelled",
        description: response.message || "Your subscription has been permanently cancelled.",
      });

      setShowCancelDialog(false);
      await refreshSubscription();
      navigate('/');
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to cancel subscription. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRenewNow = async () => {
    setIsProcessing(true);
    try {
      const response = await apiClient.post<{ ok: boolean; message: string }>(
        API_ROUTES.BILLING_RESUME
      );

      if (!response.ok) {
        throw new Error(response.message || 'Failed to resume subscription');
      }

      toast({
        title: "Subscription Reactivated",
        description: response.message || "Welcome back! Your subscription has been reactivated.",
      });

      setShowRenewDialog(false);
      await refreshSubscription();
      navigate('/dashboard');
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to reactivate subscription. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const remainingMonths = calculateRemainingMonths();

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-6">
      <div className="w-full max-w-md">
        <Card>
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">Account Paused</CardTitle>
            <CardDescription>
              Your account is currently paused and will automatically reactivate in {remainingMonths} month{remainingMonths !== 1 ? 's' : ''}.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-center text-sm text-muted-foreground">
              Once your pause period ends, you'll regain full access to your account and billing will resume automatically.
            </div>
            
            <div className="space-y-3">
              <Button 
                onClick={() => setShowRenewDialog(true)}
                className="w-full"
                size="lg"
              >
                Manage Reactivation
              </Button>
              
              <Button
                variant="outline"
                onClick={() => supabase.auth.signOut().then(() => navigate('/auth'))}
                className="w-full"
              >
                Sign Out
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Renew Confirmation Dialog */}
        <Dialog open={showRenewDialog} onOpenChange={setShowRenewDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Manage Your Subscription</DialogTitle>
              <DialogDescription>
                Choose how you'd like to proceed with your paused subscription.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <Button 
                onClick={() => {
                  setShowRenewDialog(false);
                  setShowCancelDialog(true);
                }}
                variant="outline" 
                className="w-full"
              >
                Cancel Completely
              </Button>
              <Button 
                onClick={handleRenewNow}
                disabled={isProcessing}
                className="w-full"
              >
                {isProcessing ? "Processing..." : "Renew Now"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Cancel Confirmation Dialog */}
        <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Cancel Subscription Permanently?</DialogTitle>
              <DialogDescription>
                This will permanently cancel your subscription. You will lose access to all premium features and cannot reactivate your paused subscription.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button 
                variant="outline" 
                onClick={() => setShowCancelDialog(false)}
                disabled={isProcessing}
              >
                Go Back
              </Button>
              <Button 
                onClick={handleCancelSubscription}
                disabled={isProcessing}
                variant="destructive"
              >
                {isProcessing ? "Processing..." : "Confirm Cancel"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}